import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Typography,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { RefPriceOrder } from '../../models/response/RefPrice';
import { Division } from '../components/division-input';
import Cascader from '../components/cascader';
import cascaderOptions, { DivisionUtil } from '@pansy/china-division';

const divisionUtil = new DivisionUtil(cascaderOptions);

// 扩展 RefPriceOrder 类型，添加前端使用的省市区选择字段
interface ExtendedRefPriceOrder extends Partial<RefPriceOrder> {
  fromDivision?: Division;
  toDivision?: Division;
}

interface NewReferencePriceDialogProps {
  open: boolean;
  onClose: () => void;
}

const initialFormState: ExtendedRefPriceOrder = {
  order: '',
  price: 0,
  price_type: 0,
  from: '',
  to: '',
  truck_length: undefined,
  truck_type: undefined,
  distance_linear: 0,
  fromDivision: undefined,
  toDivision: undefined,
};

// 货车类型选项
const truckTypeOptions = [
  { id: 0, name: '平板' },
  { id: 1, name: '高栏' },
  { id: 2, name: '厢式' },
  { id: 3, name: '自卸' },
  { id: 4, name: '冷藏' },
  { id: 5, name: '爬梯' },
  { id: 6, name: '集装箱' },
  { id: 7, name: '高低板' },
];

// 货车长度选项
const truckLengthOptions = [
  { id: 4.2, name: '4米2' },
  { id: 6.8, name: '6米6' },
  { id: 8.7, name: '8米7' },
  { id: 9.6, name: '9米6' },
  { id: 13, name: '13米' },
  { id: 13.75, name: '13米75' },
  { id: 17.5, name: '17米5' },
];

const NewReferencePriceDialog: React.FC<NewReferencePriceDialogProps> = ({ open, onClose }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  
  const [formValues, setFormValues] = useState<ExtendedRefPriceOrder>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (name: keyof ExtendedRefPriceOrder, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除该字段的错误
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formValues.fromDivision) {
      newErrors.fromDivision = '请选择起点';
    }
    
    if (!formValues.toDivision) {
      newErrors.toDivision = '请选择终点';
    }
    
    if (!formValues.price || formValues.price <= 0) {
      newErrors.price = '请输入有效的价格';
    }
    
    if (!formValues.distance_linear || formValues.distance_linear <= 0) {
      newErrors.distance_linear = '请输入有效的直线距离';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      // 添加时间戳并格式化起点终点
      const dataToSubmit = {
        ...formValues,
        from: formValues.fromDivision ? `${formValues.fromDivision.province}${formValues.fromDivision.city}${formValues.fromDivision.district}` : '',
        to: formValues.toDivision ? `${formValues.toDivision.province}${formValues.toDivision.city}${formValues.toDivision.district}` : '',
        time: new Date().toISOString(),
      };
      
      await dataProvider.addCargoPrice(dataToSubmit as RefPriceOrder);
      notify('参考价格添加成功', { type: 'success' });
      refresh();
      handleClose();
    } catch (error) {
      console.error('添加参考价格失败:', error);
      notify('添加参考价格失败', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormValues(initialFormState);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>添加参考价格</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>起点</Typography>
            <Cascader
              options={divisionUtil.getSourceData()}
              onChange={(value) => handleChange('fromDivision', value)}
              defaultValue={formValues.fromDivision ? [formValues.fromDivision.province, formValues.fromDivision.city, formValues.fromDivision.district] : []}
            />
            {errors.fromDivision && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.fromDivision}
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>终点</Typography>
            <Cascader
              options={divisionUtil.getSourceData()}
              onChange={(value) => handleChange('toDivision', value)}
              defaultValue={formValues.toDivision ? [formValues.toDivision.province, formValues.toDivision.city, formValues.toDivision.district] : []}
            />
            {errors.toDivision && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.toDivision}
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="参考价格(元)"
              type="number"
              fullWidth
              value={formValues.price}
              onChange={(e) => handleChange('price', Number(e.target.value))}
              error={!!errors.price}
              helperText={errors.price}
              required
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="直线距离(公里)"
              type="number"
              fullWidth
              value={formValues.distance_linear}
              onChange={(e) => handleChange('distance_linear', Number(e.target.value))}
              error={!!errors.distance_linear}
              helperText={errors.distance_linear}
              required
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>货车类型</InputLabel>
              <Select
                value={formValues.truck_type === undefined ? '' : formValues.truck_type}
                label="货车类型"
                onChange={(e) => handleChange('truck_type', e.target.value === '' ? undefined : Number(e.target.value))}
              >
                <MenuItem value=""><em>无</em></MenuItem>
                {truckTypeOptions.map(option => (
                  <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>货车长度</InputLabel>
              <Select
                value={formValues.truck_length === undefined ? '' : formValues.truck_length}
                label="货车长度"
                onChange={(e) => handleChange('truck_length', e.target.value === '' ? undefined : Number(e.target.value))}
              >
                <MenuItem value=""><em>无</em></MenuItem>
                {truckLengthOptions.map(option => (
                  <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">取消</Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewReferencePriceDialog;